import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  SiNodedotjs,
  SiPython,
  SiGo,
  SiDotnet,
  SiAngular,
  SiReact,
  SiVite,
  SiVuedotjs,
  SiNextdotjs,
  SiHtml5
} from 'react-icons/si';
import { ordersService, servicesService } from '../../services';
import { ProductCategory } from '../../constants/productCategory';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductsContext';
import { useTranslation } from 'react-i18next';
import Step1DeployApp from './steps/Step1DeployApp';
import Step2DeployApp from './steps/Step2DeployApp';
import Step3DeployApp from './steps/Step3DeployApp';
import { 
  getPlansByFilters, 
  getNetworkAndComputeModeFromPlan,
  planConfigToComponentFormat,
  getPlanById,
  DeploymentType as ConfigDeploymentType
} from '../../config/deployAppPlans.config';
import { ProjectConfig, DeploymentType } from '../../types/deployApp.type';
import ProgressSteps from './components/ProgressSteps';

const CreateDeployApp: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { products: allProducts, getProductsByCategory } = useProducts();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<ProjectConfig>({
    name: '',
    description: '',
    type: '',
    repository: '',
    branch: 'main',
    framework: '',
    environment: '',
    region: 'us-east-1',
    autoDeploy: true,
    premiumLogs: false,
    environmentVariables: [],
    deploymentType: '',
    subdomain: '',
    slug: '',
    runtime: undefined,
    startMode: undefined,
    targetMode: undefined,
    buildCommand: undefined,
  });
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedLogsPlan, setSelectedLogsPlan] = useState<string | null>(null);
  const [networkMode, setNetworkMode] = useState<'shared' | 'tenant' | null>(null);
  const [computeMode, setComputeMode] = useState<'shared' | 'dedicated' | null>(null);

  // Estado para controlar la creación de la orden
  const [isCreating, setIsCreating] = useState(false);

  // Obtener productos de la categoría APP_DEPLOY desde el contexto (cacheado)
  const products = allProducts[ProductCategory.APP_DEPLOY] || [];

  // Cargar productos solo una vez cuando el componente se monta y hay token
  useEffect(() => {
    if (accessToken && products.length === 0) {
      getProductsByCategory(ProductCategory.APP_DEPLOY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Resetear plan y auto-configurar deploymentType al cambiar tipo o framework
  useEffect(() => {
    setSelectedPlan(null);
    const isStatic = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
    setConfig(prev => ({ ...prev, deploymentType: isStatic ? 'prod' : 'dev' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.type, config.framework]);

  // Resetear networkMode y computeMode cuando cambia el tipo o deploymentType
  useEffect(() => {
    const isBackend = (isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(t => t.id === config.type)) && !isSSGTechnology(config.type, config.framework);
    if (!isBackend) {
      setNetworkMode(null);
      setComputeMode(null);
    } else {
      setNetworkMode(null);
      setComputeMode(null);
      setSelectedPlan(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.type, config.framework, config.deploymentType]);

  // Resetear runtime, startMode y targetMode cuando cambia el tipo o framework
  useEffect(() => {
    const isBackend = (isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(t => t.id === config.type)) && !isSSGTechnology(config.type, config.framework);
    if (!isBackend) {
      if (config.runtime || config.startMode || config.targetMode || config.buildCommand) {
        setConfig(prev => ({ ...prev, runtime: undefined, startMode: undefined, targetMode: undefined, buildCommand: undefined }));
      }
    } else if (config.runtime) {
      const runtimeTech = getRuntimeTechnology(config.type);
      if (config.runtime.technology !== runtimeTech) {
        setConfig(prev => ({ ...prev, runtime: undefined, startMode: undefined, targetMode: undefined, buildCommand: undefined }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.type, config.framework]);

  // Resetear plan de logs cuando se desactiva premium logs
  useEffect(() => {
    if (!config.premiumLogs) {
      setSelectedLogsPlan(null);
    }
  }, [config.premiumLogs]);

  // Tecnologías estáticas (no necesitan backend)
  const staticProjectTypes = [
    { id: 'html', name: 'HTML + JavaScript', icon: <SiHtml5 className="text-3xl" /> },
    { id: 'react', name: 'React', icon: <SiReact className="text-3xl" style={{ color: '#61DAFB' }} /> },
    { id: 'vue', name: 'Vue.js', icon: <SiVuedotjs className="text-3xl" style={{ color: '#4FC08D' }} /> },
    { id: 'angular', name: 'Angular', icon: <SiAngular className="text-3xl" style={{ color: '#DD0031' }} /> },
    { id: 'vite', name: 'Vite', icon: <SiVite className="text-3xl" style={{ color: '#646CFF' }} /> },
  ];

  // Tecnologías con backend
  const backendProjectTypes = [
    { id: 'nodejs', name: 'Node.js', icon: <SiNodedotjs className="text-3xl" style={{ color: '#339933' }} /> },
    { id: 'python', name: 'Python', icon: <SiPython className="text-3xl" style={{ color: '#3776AB' }} /> },
    { id: 'go', name: 'Go', icon: <SiGo className="text-3xl" style={{ color: '#00ADD8' }} /> },
    { id: 'netcore', name: '.NET Core', icon: <SiDotnet className="text-3xl" style={{ color: '#512BD4' }} /> },
    { id: 'nextjs', name: 'Next.js', icon: <SiNextdotjs className="text-3xl" style={{ color: '#000000' }} /> },
    { id: 'nuxt', name: 'Nuxt.js', icon: <SiVuedotjs className="text-3xl" style={{ color: '#00DC82' }} /> },
  ];

  // Función para verificar si una tecnología es estática
  const isStaticTechnology = (type: string): boolean => {
    return staticProjectTypes.some(t => t.id === type);
  };

  // Función para verificar si Next.js o Nuxt son SSR (no estáticos)
  const isSSRTechnology = (type: string, framework: string): boolean => {
    if (type === 'nextjs' && framework === 'nextjs-ssr') return true;
    if (type === 'nuxt' && framework === 'nuxt-ssr') return true;
    return false;
  };

  // Función para verificar si Next.js o Nuxt son SSG (estáticos)
  const isSSGTechnology = (type: string, framework: string): boolean => {
    if (type === 'nextjs' && framework === 'nextjs-ssg') return true;
    if (type === 'nuxt' && framework === 'nuxt-ssg') return true;
    return false;
  };

  // Función para obtener la tecnología de runtime según el tipo
  const getRuntimeTechnology = (type: string): string => {
    if (type === 'nextjs' || type === 'nuxt') {
      return 'nodejs';
    }
    if (type === 'go') {
      return 'go';
    }
    return type;
  };

  // Función para obtener las versiones disponibles según la tecnología
  const getRuntimeVersions = (technology: string): Array<{ value: string; label: string; recommended?: boolean }> => {
    switch (technology) {
      case 'nodejs':
        return [
          { value: '16', label: 'Node.js 16', recommended: false },
          { value: '18', label: 'Node.js 18', recommended: false },
          { value: '20', label: 'Node.js 20 (LTS)', recommended: false },
          { value: '22', label: 'Node.js 22 (LTS) - Recomendado', recommended: true },
          { value: '24', label: 'Node.js 24 (LTS)', recommended: false },
        ];
      case 'netcore':
        return [
          { value: '6', label: '.NET 6', recommended: false },
          { value: '8', label: '.NET 8 (LTS)', recommended: false },
          { value: '9', label: '.NET 9', recommended: false },
          { value: '10', label: '.NET 10 (LTS) - Recomendado', recommended: true },
        ];
      case 'python':
        return [
          { value: '3.9', label: 'Python 3.9', recommended: false },
          { value: '3.10', label: 'Python 3.10', recommended: false },
          { value: '3.11', label: 'Python 3.11', recommended: false },
          { value: '3.12', label: 'Python 3.12 - Recomendado', recommended: true },
          { value: '3.13', label: 'Python 3.13', recommended: false },
          { value: '3.14', label: 'Python 3.14', recommended: false },
        ];
      case 'go':
        return [
          { value: '1.21.13', label: 'Go 1.21' },
          { value: '1.22.12', label: 'Go 1.22' },
          { value: '1.23.6', label: 'Go 1.23' },
          { value: '1.24.7', label: 'Go 1.24 - Recomendado', recommended: true },
          { value: '1.25.1', label: 'Go 1.25' },
        ];
      case 'nextjs':
      case 'nuxt':
        return [
          { value: '16', label: 'Node.js 16', recommended: false },
          { value: '18', label: 'Node.js 18', recommended: false },
          { value: '20', label: 'Node.js 20 (LTS)', recommended: false },
          { value: '22', label: 'Node.js 22 (LTS) - Recomendado', recommended: true },
          { value: '24', label: 'Node.js 24 (LTS)', recommended: false },
        ];
      default:
        return [];
    }
  };

  // Función para determinar el tipo de plan (shared o dedicated)
  const getPlanType = (planId: string): 'shared' | 'dedicated' | null => {
    const plan = getPlanById(planId);
    if (plan) {
      return plan.planType;
    }
    if (planId.includes('shared')) {
      return 'shared';
    }
    if (planId.includes('assigned')) {
      return 'dedicated';
    }
    return null;
  };

  // Función para obtener los planes disponibles según la tecnología y tipo de deployment
  const getAvailablePlans = () => {
    if (!config.type || !config.deploymentType) return null;

    const isStatic = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
    const isBackend = (isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(t => t.id === config.type)) && !isSSGTechnology(config.type, config.framework);

    const appType = isStatic ? 'static' : 'backend';
    const deploymentType = config.deploymentType as ConfigDeploymentType;
    
    const plansConfig = isBackend && networkMode && computeMode
      ? getPlansByFilters(appType, deploymentType, networkMode as any, computeMode as any)
      : isBackend
      ? []
      : getPlansByFilters(appType, deploymentType);
    
    if (plansConfig.length === 0) return null;

    return {
      type: appType,
      deploymentType: deploymentType,
      plans: plansConfig.map(plan => planConfigToComponentFormat(plan, t))
    };
  };

  // Función para obtener el badge y color según el tipo de deployment
  const getDeploymentBadge = (type: DeploymentType) => {
    const badges = {
      dev: { emoji: '🟣', text: 'DEV', color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700' },
      preview: { emoji: '🟡', text: 'PREVIEW', color: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700' },
      prod_lite: { emoji: '🟢', text: 'PROD', color: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700' },
      prod: { emoji: '🔵', text: 'PROD HA', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700' }
    };
    return badges[type] || null;
  };

  // Todas las tecnologías combinadas (para compatibilidad)
  const projectTypes = [...staticProjectTypes, ...backendProjectTypes];

  const projectTypeDescriptions: Record<string, string> = {
    'html': 'Static HTML, CSS and JavaScript. Perfect for landing pages, portfolios, and marketing sites.',
    nodejs: 'Node.js is a JavaScript runtime built on Chrome\'s V8 engine. Perfect for building scalable network applications and server-side applications.',
    python: 'Python is a high-level programming language known for its simplicity and readability. Ideal for web development, data science, and automation.',
    go: 'Go is a statically typed, compiled language designed for simplicity and performance. Perfect for building high-performance APIs, microservices, and cloud-native applications.',
    netcore: '.NET Core is a cross-platform framework for building modern applications. Great for enterprise applications and microservices.',
    angular: 'Angular is a TypeScript-based web application framework. Perfect for building large-scale, enterprise-grade applications with a complete solution.',
    react: 'React is a JavaScript library for building user interfaces. Ideal for creating interactive UIs with component-based architecture.',
    vite: 'Vite is a next-generation frontend build tool. Provides lightning-fast development experience and optimized production builds.',
    vue: 'Vue.js is a progressive JavaScript framework for building user interfaces. Known for its simplicity and flexibility.',
    nextjs: 'Next.js is a React framework for production. Enables server-side rendering, static site generation, and API routes.',
    nuxt: 'Nuxt.js is a Vue.js framework for building modern web applications. Provides server-side rendering and static site generation.',
  };

  const handleNext = () => {
    if (step < 3 && isStepValid()) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    const isStaticForValidation = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
    const needsSubdomainSlug = !isStaticForValidation;
    if (
      !config.name ||
      !config.type ||
      !selectedPlan ||
      (needsSubdomainSlug && (!(config.subdomain ?? '').trim() || !(config.slug ?? '').trim()))
    ) {
      console.error('Missing required configuration');
      alert(t('deployApp.validation.missingFields') || 'Por favor completa todos los campos requeridos.');
      return;
    }

    setIsCreating(true);
    try {
      const isStatic = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
      const appType = isStatic ? 'static' : 'server';

      const selectedProduct = products.find(p => {
        const productAppType = p.appType;
        return productAppType === appType;
      });

      if (!selectedProduct || !selectedProduct.id) {
        console.error(`No product found for appType: ${appType} (available: ${products.length})`);
        alert(t('deployApp.validation.noProduct') || 'No se encontró un producto disponible. Por favor, intenta de nuevo.');
        setIsCreating(false);
        return;
      }

      const planType = selectedPlan ? getPlanType(selectedPlan) : null;
      
      const finalNetworkMode = isStatic ? 'shared' : (networkMode || 'shared');
      const finalComputeMode = isStatic ? 'shared' : (computeMode || 'shared');
      
      if (!isStatic && selectedPlan) {
        const planNetworkAndCompute = getNetworkAndComputeModeFromPlan(selectedPlan);
        if (planNetworkAndCompute) {
          if (planNetworkAndCompute.networkMode !== finalNetworkMode || planNetworkAndCompute.computeMode !== finalComputeMode) {
            console.warn('Los valores de networkMode y computeMode del plan no coinciden con los seleccionados. Usando valores seleccionados.');
          }
        }
      }

      const orderData: any = {
        productId: selectedProduct.id,
        quantity: 1,
        metadata: {
          type: 'APP_DEPLOY',
          appName: config.name,
          appType: appType,
          technology: config.framework || config.type,
          plan: selectedPlan,
          planType: planType,
          networkMode: finalNetworkMode,
          computeMode: finalComputeMode,
          subdomain: (config.subdomain || '').trim() || undefined,
          appConfig: {
            name: config.name,
            description: config.description || '',
            type: config.type,
            environment: config.environment || '',
            autoDeploy: config.autoDeploy !== undefined ? config.autoDeploy : true,
            premiumLogs: config.premiumLogs || false,
            premiumLogsPlan: selectedLogsPlan || null,
            environmentVariables: config.environmentVariables || [],
            deploymentType: config.deploymentType,
            subdomain: (config.subdomain || '').trim() || undefined,
            slug: (config.slug || '').trim() || undefined,
            runtime: config.runtime || undefined,
            startMode: appType === 'server' ? config.startMode : undefined,
            targetMode: appType === 'server' ? config.targetMode : undefined,
            buildCommand: appType === 'server' ? (config.buildCommand || undefined) : undefined,
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error: any) {
      console.error('Error creating order:', error);
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        t('deployApp.errors.createOrder') ||
        'Error al crear la orden. Por favor, intenta de nuevo.';
      alert(errorMessage);
      setIsCreating(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        const isStaticStep1 = isStaticTechnology(config.type) || isSSGTechnology(config.type, config.framework);
        const hasName = !!config.name;
        const hasType = !!config.type;
        const hasSubdomainSlug = (config.subdomain ?? '').trim() && (config.slug ?? '').trim();
        const basicValid = hasName && hasType && (isStaticStep1 || hasSubdomainSlug);
        if (!basicValid) return false;
        
        const isBackend = (isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(t => t.id === config.type)) && !isSSGTechnology(config.type, config.framework);
        if (isBackend) {
          return networkMode !== null && computeMode !== null && selectedPlan !== null;
        }
        
        return selectedPlan !== null;
      case 2:
        const isBackendStep2 = (isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(t => t.id === config.type)) && !isSSGTechnology(config.type, config.framework);
        if (isBackendStep2) {
          if (!config.runtime?.version) {
            return false;
          }
          if (!config.startMode) {
            return false;
          }
          // Para .NET con startMode auto/publish, targetMode es opcional (el script lo autodetecta).
          const isNetcoreAutoOrPublish = config.type === 'netcore' && (config.startMode === 'auto' || config.startMode === 'publish');
          if (!isNetcoreAutoOrPublish && !config.targetMode) {
            return false;
          }
        }
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const getEstimatedCost = () => {
    if (!selectedPlan) return '$0.00';
    const plan = getAvailablePlans()?.plans.find(p => p.id === selectedPlan);
    if (!plan || plan.price === null) return t('deployApp.review.customPricing') || 'Custom';

    let totalCost = plan.price;

    if (config.premiumLogs && selectedLogsPlan) {
      if (selectedLogsPlan === 'premium-7days') {
        totalCost += 5;
      } else if (selectedLogsPlan === 'premium-30days') {
        totalCost += 10;
      }
    }

    return `$${totalCost.toFixed(2)}`;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1DeployApp
            config={config}
            setConfig={setConfig}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            networkMode={networkMode}
            setNetworkMode={setNetworkMode}
            computeMode={computeMode}
            setComputeMode={setComputeMode}
            availablePlans={getAvailablePlans() ?? null}
            getDeploymentBadge={getDeploymentBadge}
            getPlanType={getPlanType}
            staticProjectTypes={staticProjectTypes}
            backendProjectTypes={backendProjectTypes}
            projectTypeDescriptions={projectTypeDescriptions}
            projectTypes={projectTypes}
            isStaticTechnology={isStaticTechnology}
            isSSRTechnology={isSSRTechnology}
            isSSGTechnology={isSSGTechnology}
            onVerifySubdomain={(name, appType) => servicesService.checkSubdomainAvailable(name, appType)}
          />
        );
      case 2:
        return (
          <Step2DeployApp
            config={config}
            setConfig={setConfig}
            selectedLogsPlan={selectedLogsPlan}
            setSelectedLogsPlan={setSelectedLogsPlan}
            getRuntimeTechnology={getRuntimeTechnology}
            getRuntimeVersions={getRuntimeVersions}
            isSSRTechnology={isSSRTechnology}
            isSSGTechnology={isSSGTechnology}
            backendProjectTypes={backendProjectTypes}
          />
        );
      case 3:
        return (
          <Step3DeployApp
            config={config}
            projectTypes={projectTypes}
            getDeploymentBadge={getDeploymentBadge}
            selectedPlan={selectedPlan}
            availablePlans={getAvailablePlans() ?? null}
            getPlanType={getPlanType}
            selectedLogsPlan={selectedLogsPlan}
            isSSRTechnology={isSSRTechnology}
            isSSGTechnology={isSSGTechnology}
            backendProjectTypes={backendProjectTypes}
            getEstimatedCost={getEstimatedCost}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-6">
      <button
        onClick={() => navigate('/deploy-apps')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
      >
        <ArrowLeftOutlined />
        <span className="text-sm font-medium">Back to Deploy Apps</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('deployApp.title') || 'Deploy App'}</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">{t('deployApp.subtitle') || 'Deploy your application from a Git repository'}</p>

      <ProgressSteps currentStep={step} />

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className={`px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors ${step === 1
            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
        >
          {t('deployApp.actions.back') || 'Back'}
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/deploy-apps')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('deployApp.actions.cancel') || 'Cancel'}
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${isStepValid()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
            >
              {t('deployApp.actions.next') || 'Next'}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? t('deployApp.actions.creating') || 'Creating order...' : t('deployApp.actions.create') || 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateDeployApp;